import { KBarPositioner, KBarAnimator, KBarSearch } from "kbar";
import styled from "styled-components";
import { FontFamily } from "@themes/index";
import { BREAKPOINTS } from "constants/breakpoints";

// Global Search Container Styles
export const StyledKBarPositioner = styled(KBarPositioner)`
    position: fixed;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    inset: 0px;
    padding: 14vh 16px 16px;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    z-index: 99;
`;

export const StyledKBarAnimator = styled(KBarAnimator)`
    max-width: 600px;
    width: 100%;
    background: var(--modal-background);
    color: var(--modal-text);
    border-radius: 16px;
    overflow: hidden;
    box-shadow:
        0 25px 50px -12px rgba(0, 0, 0, 0.25),
        0 0 0 1px rgba(255, 255, 255, 0.1);
    animation: searchModalSlideIn 0.2s ease-out;
    transform-origin: top center;

    @keyframes searchModalSlideIn {
        from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
        }
        to {
            opacity: 1;
            transform: translateY(0) scale(1);
        }
    }

    @media (max-width: ${BREAKPOINTS.smallTablet}) {
        max-width: 100%;
    }
`;

export const StyledKBarSearch = styled(KBarSearch)`
    padding: 20px 24px;
    font-size: 16px;
    width: 100%;
    box-sizing: border-box;
    outline: none;
    border: none;
    background: transparent;
    color: var(--input-text);
    font-family: var(--font-family, "Poppins", sans-serif);
    font-weight: 400;

    &::placeholder {
        color: var(--input-placeholder);
        font-weight: 400;
    }

    &:focus {
        outline: none;
    }
`;

export const SearchContainer = styled.div`
    position: relative;
    border-bottom: 1px solid var(--table-border);

    &::before {
        content: "";
        position: absolute;
        left: 24px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23667085'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'%3E%3C/path%3E%3C/svg%3E");
        background-size: contain;
        background-repeat: no-repeat;
        opacity: 0.6;
    }

    ${StyledKBarSearch} {
        padding-left: 56px;
    }
`;

export const SearchFooter = styled.div`
    padding: 12px 16px;
    border-top: 1px solid var(--table-border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 12px;
    color: var(--secondary-text);
`;

export const KeyboardShortcuts = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;

    font-family: ${FontFamily.poppins};
    font-size: 12px;

    @media (max-width: ${BREAKPOINTS.smallTablet}) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

export const ShortcutKey = styled.kbd`
    padding: 2px 6px;
    background: var(--tool-button-fill);
    border: 1px solid var(--tool-button-stroke);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    color: var(--tool-button-text);
    font-family: inherit;
`;

// Results Container Styles
export const ResultsContainer = styled.div`
    max-height: 420px;
    overflow-y: auto;
    padding: 8px;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: var(--input-border);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--secondary-text);
    }

    @media (max-width: 480px) {
        max-height: 300px;
        padding: 4px;
    }
`;

export const SectionHeader = styled.div`
    padding: 12px 16px 8px;
    font-size: 12px;
    font-weight: 600;
    font-family: ${FontFamily.poppins};
    color: var(--secondary-text);
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

export const ResultItem = styled.div<{ $active: boolean }>`
    padding: 12px 16px;
    margin: 2px 8px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 400;
    color: var(--text-base);
    background: ${props => (props.$active ? "var(--primary-button-background)" : "transparent")};
    color: ${props => (props.$active ? "var(--primary-button-text)" : "var(--text-base)")};
    transition: all 0.15s ease;
    font-family: ${FontFamily.poppins};
    outline: none;
    position: relative;

    &:hover {
        background: ${props => (props.$active ? "var(--primary-button-background)" : "var(--highlight-block)")};
    }

    &:focus-visible {
        outline: 2px solid var(--primary-button-background);
        outline-offset: 2px;
    }

    @media (max-width: 480px) {
        padding: 10px 12px;
        margin: 1px 4px;
        font-size: 13px;
    }
`;

export const ShortcutBadge = styled.div`
    margin-left: auto;
    padding: 4px 8px;
    background: var(--tool-button-fill);
    border: 1px solid var(--tool-button-stroke);
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    color: var(--tool-button-text);
    display: flex;
    align-items: center;
    gap: 2px;

    @media (max-width: 480px) {
        padding: 2px 6px;
        font-size: 10px;
    }
`;

export const NoResults = styled.div`
    padding: 32px 16px;
    text-align: center;
    color: var(--secondary-text);
    font-size: 14px;
    font-family: ${FontFamily.poppins};
`;
